// Which objects stand against which walls, and which sit on the floor. Moved
// verbatim out of roomTakeOffs.js.

const { getObjectType } = require('../fixture-types');
const { categoryOf } = require('../split-level/elementGeometry');
const { objAgainstWall, objPerpDistToWall } = require('./wallFrame');

// Floor level for objects (must be sitting on floor): 10 cm allows for
// scan jitter on a vanity / lower cabinet base.
const FLOOR_LEVEL_TOLERANCE_M = 0.1;
// Floor level for openings (must go all the way to the floor): tighter,
// since the lowest a window sill can plausibly sit is ~30 cm — 5 cm is
// generous enough for scan jitter on door thresholds without catching
// short low windows.
const OPENING_FLOOR_TOLERANCE_M = 0.05;

// Floor reference Y for a room: pretransformed RoomPlan data is in scan
// coords where the floor isn't at y=0, so compare against the floor's
// own y. Falls back to the min wall floor-edge when no floor element
// has a usable center, then to 0.
const getRoomFloorY = (room) => {
  const fy = room?.floors?.[0]?.center?.y;
  if (typeof fy === 'number') return fy;
  const walls = room?.walls || [];
  let best = null;
  for (const w of walls) {
    const cy = w?.center?.y;
    const dy = w?.dimensions?.y;
    if (typeof cy === 'number' && typeof dy === 'number') {
      const edge = cy - dy / 2;
      if (best === null || edge < best) best = edge;
    }
  }
  return best ?? 0;
};

const isAtFloorLevel = (obj, floorY) => {
  const cy = obj?.center?.y;
  const oh = obj?.dimensions?.y;
  if (typeof cy !== 'number' || typeof oh !== 'number') return false;
  return cy - oh / 2 - floorY <= FLOOR_LEVEL_TOLERANCE_M;
};

// "storage" = RoomPlan generic-storage class (freestanding shelves /
// dressers / wardrobes); not cabinetry.
// "blank-panel" = decorative filler that sits *next to* a corner cabinet;
// the corner cabinet itself already accounts for that span, so counting
// the blank panel separately would double-subtract. Excluded.
const isCabinetType = (t) =>
  t === 'lower-cabinet' ||
  t === 'upper-cabinet' ||
  t === 'storage-cabinet' ||
  t === 'vanity' ||
  t === 'corner-cabinet';
const isLowerCabinetType = (t) =>
  t === 'lower-cabinet' ||
  t === 'storage-cabinet' ||
  t === 'vanity' ||
  t === 'corner-cabinet';
const isTubOrShower = (t) => t === 'bathtub' || t === 'tub' || t === 'shower';
// Major kitchen appliances. Per current rules they:
//   - do NOT cut tile (you'd tile under them; they're removable);
//   - cut baseboard ONLY on the wall their back is against, not on a
//     side wall they happen to sit next to;
//   - do NOT cut paint area (the wall behind them stays painted).
// In other words: appliances behave differently from cabinets and are
// handled via single-closest-wall assignment + baseboard-only.
const isApplianceType = (t) =>
  t === 'fridge' ||
  t === 'refrigerator' ||
  t === 'stove' ||
  t === 'range' ||
  t === 'cooktop' ||
  t === 'oven' ||
  t === 'dishwasher';

// "Tile / Paint blocker" — built-in cabinet or fixture (cut tile +
// baseboard + paint).
const isFloorBlocker = (obj, floorY) => {
  const t = (getObjectType(obj) || '').toLowerCase();
  if (!(isLowerCabinetType(t) || isTubOrShower(t))) return false;
  return isAtFloorLevel(obj, floorY);
};

// Corner-cabinet variants come in two shapes:
//   - 'single-sided': a regular rectangle the size of dim.x × dim.z.
//   - L-shape (default): two perpendicular legs forming an L. The bbox
//     is full dim.x × dim.z but the inner corner of size
//     (dim.x - cornerBox) × (dim.z - cornerBox) is empty floor.
// Standard cornerBox is 24" (the cabinet depth).
const CORNER_BOX_DEFAULT_M = 24 * 0.0254;
const isLShapeCornerCabinet = (obj) => {
  const t = (getObjectType(obj) || '').toLowerCase();
  if (t !== 'corner-cabinet') return false;
  const variant =
    obj?.dimensions?.cornerVariant ?? obj?.attributes?.cornerVariant;
  return variant !== 'single-sided';
};

/**
 * The floor a blocker covers, in m² — what the floor take-off leaves out of
 * the new floor, and the floor demolition out of the old one where the
 * blocker stays (`labor/demoScope.js`).
 */
const floorFootprintM2 = (obj) => {
  const fx = obj?.dimensions?.x;
  const fz = obj?.dimensions?.z;
  if (typeof fx !== 'number' || typeof fz !== 'number') return 0;
  if (isLShapeCornerCabinet(obj)) {
    const cb =
      obj?.dimensions?.cornerBox ??
      obj?.attributes?.cornerBox ??
      CORNER_BOX_DEFAULT_M;
    const innerW = Math.max(0, fx - cb);
    const innerD = Math.max(0, fz - cb);
    return Math.max(0, fx * fz - innerW * innerD);
  }
  return fx * fz;
};
// Wall-paint blocker (NOT appliances).
const isWallBlocker = (obj) => {
  const t = (getObjectType(obj) || '').toLowerCase();
  return isCabinetType(t) || isTubOrShower(t);
};
// Appliance baseboard-only blocker (back wall only).
const isApplianceBlocker = (obj, floorY) => {
  const t = (getObjectType(obj) || '').toLowerCase();
  if (!isApplianceType(t)) return false;
  return isAtFloorLevel(obj, floorY);
};

/**
 * Does an object standing against a wall take that wall's `kind` of finish
 * ('tile' | 'paint') out of the bill? The ONE rule the take-off cuts by and
 * the elevation drawing hides finish by, so what is drawn is what is billed
 * (Yu-Han, 2026-10-22). A wall blocker (`isWallBlocker` — cabinetry, a
 * vanity, a tub, a shower) cuts paint; it cuts tile too, but for a shower,
 * whose own tile covers its footprint. An appliance cuts neither (the wall
 * behind it is tiled and painted), and nor does a toilet — not a blocker at
 * all: the tile and paint behind its tank go on before it is set.
 */
const blockerCutsFinish = (obj, kind) => {
  if (!isWallBlocker(obj)) return false;
  const t = (getObjectType(obj) || '').toLowerCase();
  return kind === 'paint' || t !== 'shower';
};

/**
 * Which walls each blocker stands against (today's `wallBlockerSet`):
 * cabinets, tubs and showers on EVERY wall they touch; a floor appliance on
 * only the nearest wall it touches (its back).
 */
const assignWallBlockers = (walls, objects, floorY) => {
  const byWall = new Map();
  for (const wall of walls) byWall.set(wall, []);
  for (const obj of objects || []) {
    if (isWallBlocker(obj)) {
      for (const wall of walls) {
        if (objAgainstWall(obj, wall)) byWall.get(wall).push(obj);
      }
    } else if (isApplianceBlocker(obj, floorY)) {
      let bestWall = null;
      let bestPerp = Infinity;
      for (const wall of walls) {
        if (!objAgainstWall(obj, wall)) continue;
        const p = objPerpDistToWall(obj, wall);
        if (p < bestPerp) {
          bestPerp = p;
          bestWall = wall;
        }
      }
      if (bestWall) byWall.get(bestWall).push(obj);
    }
  }
  return byWall;
};

const isFloorLevelOpening = (o, floorY) => {
  const cy = o?.center?.y;
  const oh = o?.dimensions?.y;
  if (typeof cy !== 'number' || typeof oh !== 'number') return false;
  return cy - oh / 2 - floorY <= OPENING_FLOOR_TOLERANCE_M;
};

/** A fireplace stops baseboard where it meets the wall. */
const isFireplace = (obj) =>
  (getObjectType(obj) || '').toLowerCase() === 'fireplace';

/** Stairs stop it too; they get a skirt board instead (not counted yet). */
const isStairs = (obj) =>
  categoryOf(obj) === 'stairs' ||
  (getObjectType(obj) || '').toLowerCase() === 'stairs';

/** Wall finishes that stop baseboard where they reach the floor (spec §2). */
const BASEBOARD_STOP_FINISHES = new Set(['tile', 'slab', 'panel', 'wood']);

/**
 * Does a blocker `assignWallBlockers` put on this wall stop its baseboard?
 * The take-off's own test, unchanged: a floor appliance (already only on its
 * back wall), or a lower cabinet / vanity / tub / shower at floor level.
 */
const blockerStopsBaseboard = (obj, floorY) =>
  isApplianceType((getObjectType(obj) || '').toLowerCase()) ||
  isFloorBlocker(obj, floorY);

module.exports = {
  FLOOR_LEVEL_TOLERANCE_M,
  OPENING_FLOOR_TOLERANCE_M,
  getRoomFloorY,
  isAtFloorLevel,
  isCabinetType,
  isLowerCabinetType,
  isTubOrShower,
  isApplianceType,
  isFloorBlocker,
  isLShapeCornerCabinet,
  floorFootprintM2,
  isWallBlocker,
  isApplianceBlocker,
  blockerCutsFinish,
  isFloorLevelOpening,
  assignWallBlockers,
  isFireplace,
  isStairs,
  BASEBOARD_STOP_FINISHES,
  blockerStopsBaseboard,
};
